import { ChangeDetectorRef, Component } from '@angular/core';
import { FormArray, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { ComponentBase } from '@drl/core/component-base';
import { IpsTranslateService } from '@drl/core/localization/ips-translate.service';
import { Role } from '@drl/core/store/models/role.model';
import { SystemStoreFacadeService } from '@drl/core/store/system-store-facade.service';
import { NbDialogRef } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { CountryISO, SearchCountryField, TooltipLabel } from 'ngx-intl-tel-input';

import { User } from '../../../core/auth/models/user.model';
import { UserFacadeService } from '../../../shared/facades/users-facade.service';
import { RoleType } from '@drl/core/enums';
import { ConfirmationService, SelectItem } from 'primeng/api';
import { AsYouType, PhoneNumber, getCountryCallingCode } from 'libphonenumber-js';
import { UserSessionStoreService as StoreService } from '@drl/core/store/user-session-store.service';
import { Specialty } from '@drl/shared/utility/speciality.enum';
import { AppointmentType, ApptTypeMap } from '@drl/shared/utility/appointment-type.enum';
import { Appointment, Patient } from '@drl/shared/models';
import { PatientFacadeService } from '../../patients/patients-facade.service';
import * as moment from 'moment';
import { Prescriptions } from '../../patients/shared/models';
import { map } from 'lodash-es';
import { RouteMap } from '@drl/shared/utility/patients-caregiver-utility.enum';

@Component({
  selector: 'app-add-new-record',
  templateUrl: './add-prescription-record-modal.html',
  styleUrls: []
})
export class AddNewPrescriptionRecordComponent extends ComponentBase {
  country = CountryISO.India;
  constructor(
    protected dialogRef: NbDialogRef<AddNewPrescriptionRecordComponent>,
    private readonly userFacade: UserFacadeService,
    private readonly systemStoreFacade: SystemStoreFacadeService,
    protected readonly translateSvc: IpsTranslateService,
    private readonly store: StoreService,
    protected readonly patientFacade: PatientFacadeService,
    private readonly confirmationService: ConfirmationService,
  ) {
    super();
    this.translate = this.translateSvc.translate;
  }
  
  translate: TranslateService;
  isPhonePristine = true;
  userForm = new FormGroup({
    phone: new FormControl('9910343381'),
    extId: new FormControl(''),
  });
  specialists: User[];
  apptToEdit: Appointment
  selectedApptType: SelectItem;
  defaultCountryISO: CountryISO;
  appt: Appointment;
  prescription: Prescriptions;
  prescriptionForm = new FormGroup({
    prescriptionName: new FormControl(''),
    patientName: new FormControl(''),
    mobileNumber: new FormControl(''),
    doctorName: new FormControl('', Validators.required),
    doctorSpecialty: new FormControl('', Validators.required),
    prescriptionDate: new FormControl(new Date(), Validators.required),
  });

  medicineForm = new FormGroup({
    medicineName: new FormControl('', Validators.required),
    startDate: new FormControl(new Date(), Validators.required),
    duration: new FormControl('', Validators.required),
    dosage: new FormControl(''),
    frequency: new FormControl('', Validators.required),
    route: new FormControl('', Validators.required),
    description: new FormControl('', Validators.required),
  });

  separateDialCode = true;
  preferredCountries: CountryISO[] = [
    CountryISO.UnitedStates,
    CountryISO.India,
  ];
  SearchCountryField = SearchCountryField;
  TooltipLabel = TooltipLabel;
  CountryISO = CountryISO;
  searchBy: string = 'phone';
  isEdit = false;
  phoneEditClass() {
    return `${this.isEdit ? 'input-group input-group-edit' : 'input-group'}`;
  }

  user: User;
  users: SelectItem[] = [];
  speciality: SelectItem[] = [];
  apptTypes: SelectItem[] = [];
  invalidPatient: boolean = false;
  minDate = new Date(moment().subtract(1, 'days').toDate());
  maxDate = new Date(moment().add(1, 'years').toDate());
  loading = false;
  patientId: number;
  patientPhoneNumber: string;
  selectedDate: Date;
  selectedTime: string;
  selectedRole: Role;
  uniqEditId: number;
  roles: Role[];
  userLimit = 1000;
  defaultVenue = 'Hospital';
  canMobileNumEdit: boolean = true;
  cancelLbl: string;
  routes = map(RouteMap, (cat, key) => {
        return {
          label: cat.label,
          value: key,
        };
  });

  medErrorStates = {
    isFormSubmitted: false,
    fields: {
      name: false,
      startDate: false,
      duration: false,
      dosage: false,
      frequency: false,
      route: false,
      description: false,
    }
  };

  private readonly specialities = [
    Specialty.Nutritionist,
    Specialty.Oncologist,
    Specialty.Psychologist,
    Specialty.Others
  ];

  private readonly appointmentTypes = [
    AppointmentType.Chemotherapy,
    AppointmentType.DoctorVisit,
    AppointmentType.LabVisit,
    AppointmentType.Radiotherapy,
    AppointmentType.Surgery,
    AppointmentType.Oral,
    AppointmentType.Integral,
    AppointmentType.Nuclear
  ];

  prescriptionArray = [];
  temporaryPrescriptions: { [key: number]: any } = {};
  newPrescription1 = {
      name: '',
      startDate: '',
      duration: '',
      dosage: '',
      frequency: '',
      route: '',
      description: '',
      editable: false,
      timeToTake: ''
    };
  
onDateChange(event: any) {
  this.newPrescription1.startDate = event;
}

addNewMedicine() {
  if (this.medicineForm.invalid) {
    this.medicineForm.markAllAsTouched(); // <- this line is key
    return;
  }
  const hours = this.medicineForm.controls['startDate'].value.getHours().toString().padStart(2, "0"); // Ensures two-digit format
  const minutes = this.medicineForm.controls['startDate'].value.getMinutes().toString().padStart(2, "0");


  const formattedTime = `${hours}:${minutes}:00`;
  this.prescriptionArray.push({ 
    name: this.medicineForm.controls['medicineName'].value,
    startDate: this.medicineForm.controls['startDate'].value,
    duration: this.medicineForm.controls['duration'].value,
    dosage: this.medicineForm.controls['dosage'].value,
    frequency: this.medicineForm.controls['frequency'].value,
    route: this.medicineForm.controls['route'].value,
    description: this.medicineForm.controls['description'].value,
    editable: false,
    timeToTake: formattedTime
   });
  this.medicineForm.reset();
  this.medicineForm.patchValue({
    startDate: new Date(),
  });
}

toggleEditPrescription(index: number) {
  const prescription = this.prescriptionArray[index];

  if (!prescription.editable) {
    // Clone fresh data when entering edit mode
    this.temporaryPrescriptions[index] = { ...prescription };
    this.prescriptionArray[index].editable = true;
  } else {
    // Cancel editing
    this.prescriptionArray[index].editable = false;
    delete this.temporaryPrescriptions[index];
  }
}


editPrescription(index: number) {
  this.prescriptionArray[index] = {
    ...this.temporaryPrescriptions[index],
    editable: false
  };
  delete this.temporaryPrescriptions[index];
}

  removePrescription(index: number) {
    this.prescriptionArray.splice(index, 1);
    delete this.temporaryPrescriptions[index];
  }

  deleteConfirm(index: number) {
      this.confirmationService.confirm({
        key: 'updateConfirm',
        message: 'Are you sure you want to delete the medicine?',
        accept: () => {
          this.removePrescription(index);
        },
      });
  }


  checkErrorState() {
    this.medErrorStates.isFormSubmitted = true;
    if(!this.medErrorStates.fields.description){
      this.medErrorStates.fields.description = true;
    }
    if(!this.medErrorStates.fields.name){
      this.medErrorStates.fields.name = true;
    }
    if(!this.medErrorStates.fields.startDate){
      this.medErrorStates.fields.startDate = true;
    }
    if(!this.medErrorStates.fields.duration){
      this.medErrorStates.fields.duration = true;
    }
    if(!this.medErrorStates.fields.dosage){
      this.medErrorStates.fields.dosage = true;
    }
    if(!this.medErrorStates.fields.frequency){
      this.medErrorStates.fields.frequency = true;
    }
    if(!this.medErrorStates.fields.route){
      this.medErrorStates.fields.route = true;
    }
  }


  ngOnInit() {
    super.ngOnInit();
    this.loading = true;
    this.getRoles().subscribe((roles) => {
      this.roles = roles;
      this.getSpecialists().subscribe(
        res => {
          this.specialists = res;
          if (this.prescription) {
            this.isEdit = true;
            this.setUpEditPrescription();
          }
          this.loading = false;
          res.forEach((user) => {
            this.users.push({
              label: user.fullName,
              value: user.id
            })
          });
        },
        null
      );
    });
    this.setCountry();
    this.getAppointmentType();
    this.getSpeciality();
  }

  setUpEditPrescription() {
    this.isEdit = true;
    this.canMobileNumEdit = true;
    this.loading = true;
    if (this.prescription.searchBy === 'phone') {
      this.searchBy = 'phone';
    } else {
      this.searchBy = 'extId';
    }
    const asYouType = new AsYouType();
    asYouType.input(this.prescription.mobile);
    this.userForm = new FormGroup({
      phone: new FormControl(
        {
          number: asYouType.getNumber().nationalNumber,
          countryCode: asYouType.getNumber().country || 'US',
        },
        [Validators.required],
      ),
      extId: new FormControl(this.prescription.patientExternalId),
    });
    this.setPhone();
    this.uniqEditId = this.prescription.uniqId;
    this.patientPhoneNumber = this.prescription.mobile;
    this.patientId = this.prescription.patientId;
    this.prescriptionForm = new FormGroup({
      doctorName: new FormControl(Number(this.prescription.doctorUserId), Validators.required),
      doctorSpecialty: new FormControl(this.prescription.doctorSpecialty, Validators.required),
      prescriptionDate: new FormControl(new Date(this.prescription.prescriptionDate), Validators.required),
      prescriptionName: new FormControl(this.prescription.name),
      patientName: new FormControl(this.prescription.patientName, Validators.required),
    });
    this.prescriptionArray = this.prescription.medicines;
    this.prescriptionArray.forEach(p => p.editable = false);
    this.temporaryPrescriptions = {};
    this.loading = false;
    // })
  }

  setCountry() {
    const countryName = 'india';
    this.country = CountryISO.India;
  }

  getSpecialists() {
    const specialistRole = this.roles.filter((role) => {
      return role.roleType === RoleType.Specialist
    });
    return this.userFacade
      .getUsers(
        this.userLimit,
        null,
        null,
        null,
        null,
        null,
        null,
        [specialistRole[0].id]
      )
  }

  findUserByPhoneNumber() {
    this.invalidPatient = false;
    this.user = null;
    this.loading = true;
    const patientRole = this.roles.filter((role) => {
      return role.roleType === RoleType.Patient;
    })
    this.userFacade.getPatient(
      patientRole[0].id,
      this.userForm.controls['phone'].value.e164Number,
    ).subscribe((res) => {
      this.loading = false;
      if (res.length === 0) {
        this.invalidPatient = true;
        this.appt = null;
        return;
      }
      this.user = res[0];
      this.patientPhoneNumber = this.user.phone;
      const newPrescription = new Prescriptions();
      newPrescription.uniqId = this.prescription?.uniqId;
      newPrescription.patientName = this.user.fullName;
      this.prescription = newPrescription;
      this.prescriptionForm = new FormGroup({
        prescriptionName: new FormControl('', Validators.required),
        patientName: new FormControl(this.user.fullName, Validators.required),
        mobileNumber: new FormControl(this.user.phone, Validators.required),
        doctorName: new FormControl('', Validators.required),
        doctorSpecialty: new FormControl('', Validators.required),
        prescriptionDate: new FormControl(new Date(), Validators.required),
      });
      this.getPatientDetailsByExternalId(this.user.externalId);
    })
  }

  findUserByExtId() {
    this.invalidPatient = false;
    this.user = null;
    this.loading = true;
    const patientRole = this.roles.filter((role) => {
      return role.roleType === RoleType.Patient;
    })
    this.userFacade.getPatient(
      patientRole[0].id,
      null,
      this.userForm.controls['extId'].value,
    ).subscribe((res) => {
      this.loading = false;
      if (res.length === 0) {
        this.invalidPatient = true;
        this.appt = null;
        return;
      }
      this.user = res[0];
      this.patientPhoneNumber = this.user.phone;
      const newPrescription = new Prescriptions();
      newPrescription.uniqId = this.prescription?.uniqId;
      newPrescription.patientName = this.user.fullName;
      this.prescription = newPrescription;
      this.prescriptionForm = new FormGroup({
        prescriptionName: new FormControl(''),
        patientName: new FormControl(this.user.fullName, Validators.required),
        mobileNumber: new FormControl(this.user.phone, Validators.required),
        doctorName: new FormControl('', Validators.required),
        doctorSpecialty: new FormControl('', Validators.required),
        prescriptionDate: new FormControl(new Date(), Validators.required),
      });
      this.getPatientDetailsByExternalId(this.user.externalId);
    })
  }

  getRoles() {
    return this.systemStoreFacade.getRoles();
  }

  getPatientDetailsByExternalId(id: string) {
    this.loading = true;
    this.patientFacade.getPatientCGExternalDetails(id).subscribe((res) => {
      this.patientId = res.patient.id;
      this.specialists.forEach((user) => {
        if (user.id === res.patient.specialistId) {
          this.prescriptionForm.patchValue({
            doctorName: res.patient.specialistId
          });
          this.prescriptionForm.patchValue({
            doctorSpecialty: 'Oncologist'
          });
        }
      });
      this.loading = false;
    });
  }

  getSpeciality() {
    this.specialities.forEach((spclty) => {
      this.speciality.push({
        label: this.translate.instant(spclty),
        value: spclty,
        disabled: spclty !== 'Oncologist',
      })
    })
  }

  getAppointmentType() {
    const apptsTypes = ApptTypeMap(this.translateSvc.translate);
    this.appointmentTypes.forEach((type) => {
      this.apptTypes.push({
        label: apptsTypes[type].label,
        value: type
      })
    })
  }

  savePrescription() {
    if (this.prescriptionForm.invalid) {
      return;
    }
    const doctorUserId = this.prescriptionForm.controls['doctorName'].value;
    this.prescription.doctorSpecialty = this.prescriptionForm.controls['doctorSpecialty'].value;
    this.prescription.prescriptionDate = this.prescriptionForm.controls['prescriptionDate'].value;
    this.prescription.doctorName = this.getSpecialistNameFromId(doctorUserId);
    this.prescription.patientId = this.patientId;
    this.prescription.patientName = this.prescriptionForm.controls['patientName'].value;
    this.prescription.mobile = this.patientPhoneNumber;
    this.prescription.name = this.prescriptionForm.controls['prescriptionName'].value;
    this.prescription.prescDateTimeString = moment(this.prescriptionForm.controls['prescriptionDate'].value).format('YYYY-MM-DD HH:mm:ss');
    this.prescription.medicines = this.prescriptionArray;
    this.prescription.searchBy = this.searchBy;
    this.prescription.doctorUserId = doctorUserId;
    this.prescription.patientExternalId = this.userForm.controls['extId'].value;
    this.prescription.uniqId = Date.now();
    this.prescription.patientExternalId = this.user?.externalId ||
      this.prescription.patientExternalId;
    if (this.isEdit) {
      this.prescription.uniqId = this.uniqEditId;
    }
    this.dialogRef.close({
      error: false,
      prescription: this.prescription,
    });
  }

  onRadioBtnChange(type: string) {
    this.searchBy = type;
    if (this.userForm.controls['phone'])
      this.userForm.controls['phone'].setValue('');
    if (this.userForm.controls['extId'])
      this.userForm.controls['extId'].setValue('');
    this.appt = null;
  }

  setPhone() {
    if (this.prescription.mobile) {
      this.userForm.controls.phone.setValue(
        this.asYouTypePhoneManager(this.prescription.mobile),
      );
    }
  }

  asYouTypeManager(phoneNumber) {
    const asYouType = new AsYouType();
    asYouType.input(`${phoneNumber.includes('+') ? '' : '+'}${phoneNumber}`);

    return new FormGroup({
      phone: new FormControl(
        {
          number: asYouType.getNumber().nationalNumber,
          countryCode: 'IN',
          dialCode: getCountryCallingCode(
            'IN',
          ),
        },
        [Validators.required],
      ),
    });
  }

  asYouTypePhoneManager(phoneNumber) {
    const asYouType = new AsYouType();
    asYouType.input(`${phoneNumber.includes('+') ? '' : '+'}${phoneNumber}`);
    const tempDialCode = getCountryCallingCode(
      'IN',
    );
    const dialCode = `${tempDialCode.includes('+') ? '' : '+'}${tempDialCode}`;
    return {
      number:
        (asYouType.getNumber() || ({} as PhoneNumber)).nationalNumber || '',
      countryCode:
        'IN',
      dialCode: 'IN',
    };
  }

  getSpecialistNameFromId(id: number) {
    const result = this.specialists.filter(user => user.id === id);
    return result[0].fullName;
  }

  close() {
    this.dialogRef.close();
  }

  isToday(date: Date) {
    const inputDate = moment(date); // Convert the input to a Moment object
    return inputDate.isSame(moment(), 'day');
  }
  getTimeSlots() {
    const timeSlots = [];
    let currentDateTime = this.isToday(this.selectedDate) ? moment() : moment(this.selectedDate);
    currentDateTime = this.roundToNearest5Minutes(currentDateTime);
    let endDateTime = moment(this.selectedDate).endOf('D')
    // Loop until currentTime reaches endTime
    while (currentDateTime.isBefore(endDateTime) || currentDateTime.isSame(endDateTime)) {
      // Format time as HH:mm
      const formattedTime = currentDateTime.format('HH:mm');
      timeSlots.push(formattedTime);

      // Add 5 minutes to currentTime
      currentDateTime.add(5, 'minutes');
    }

    return timeSlots;
  }
  roundToNearest5Minutes(time) {
    const minutes = time.minutes();
    const roundedMinutes = Math.round(minutes / 5) * 5;
    return time.clone().minutes(roundedMinutes).seconds(0);
  }
  timeSlots = this.getTimeSlots();
  selectedTimeslot: string;
}
